/**
 * Minimal URL routing — enough to make a view shareable, no dependency.
 *
 * This app deliberately has no router library, and adding one to move a tab
 * name and a session id into the address bar would be out of proportion. What
 * it needs is narrow: the URL should describe what you are looking at, the back
 * button should work, and a link should reopen the same view for someone else.
 *
 * Real paths rather than a `#` fragment, because nginx already falls back to
 * index.html for unknown paths (`try_files $uri $uri/ /index.html`), and Google
 * Sign-In authorises an ORIGIN, not a path — so `/sessions/m_7f3a` is served,
 * renders, and signs in exactly like `/`.
 *
 * A shared link carries no access. The recipient still signs in with Google and
 * still has to be in `admin_users`, because every byte on the page comes from
 * the engine's `/admin/*` API, which checks that per request. Sharing a URL
 * shares a POINTER, never the data.
 */

import { useEffect, useState } from "react";
import type { TaskEnv } from "./types";

export const TAB_KEYS = [
  "analytics",
  // Routable but deliberately absent from TABS in main.tsx, so it has a URL and
  // a back button without taking a slot in the nav. Reached from a link at the
  // bottom of Analytics: it answers a question asked once, rather than a number
  // you watch.
  "report",
  "waitlist",
  "registrations",
  "requests",
  "allowlist",
  "users",
  "sessions",
  "tasks",
  "latency",
  "admins",
] as const;

export type TabKey = (typeof TAB_KEYS)[number];

export interface Route {
  tab: TabKey;
  /** A waitlist submission selected by an operations notification link. */
  waitlistId: string;
  /** A live session's meeting_session_id, when one is open. */
  msid: string;
  /** The Users tab's lookup — a phone number or user id. */
  q: string;
  /**
   * The Tasks and Latency tabs' stack. In the URL because "the dev queue is backed up" is a
   * thing you send someone a link to, and the two environments are otherwise
   * indistinguishable once the page is open.
   */
  env: TaskEnv;
  /** Latency tab: whose turns — new users (first contact within 7 days of the
   *  turn) or everyone. Default "new". */
  users: LatencyUsers;
  /** Latency tab: which turns — all, or only the first reply after idle (the
   *  conversation sat quiet 30+ min and had to be reloaded). Default "all". */
  turns: LatencyTurns;
}

export type LatencyUsers = "new" | "all";
export type LatencyTurns = "all" | "idle";

const DEFAULT_TAB: TabKey = "waitlist";

/** Production is the default so that an unqualified /tasks link never sends
 *  someone to the dev queue and lets them conclude prod is idle. */
const DEFAULT_TASK_ENV: TaskEnv = "production";

function isTab(value: string): value is TabKey {
  return (TAB_KEYS as readonly string[]).includes(value);
}

/** Read the current route out of the address bar. */
export function parseRoute(): Route {
  const segments = window.location.pathname.split("/").filter(Boolean);
  const params = new URLSearchParams(window.location.search);
  const head = segments[0] ?? "";
  const tab = isTab(head) ? head : DEFAULT_TAB;
  return {
    tab,
    waitlistId: tab === "waitlist" ? (params.get("id") ?? "") : "",
    // /sessions/{msid} — decoded, since an id could in principle be escaped.
    msid: tab === "sessions" && segments[1] ? decodeURIComponent(segments[1]) : "",
    q: tab === "users" ? (params.get("q") ?? "") : "",
    // Anything that is not the literal "development" reads as production, so a
    // mangled link degrades to the safe view rather than to an empty one.
    env:
      (tab === "tasks" || tab === "latency") && params.get("env") === "development"
        ? "development"
        : DEFAULT_TASK_ENV,
    // Same degrade-to-default rule: only the exact non-default value counts.
    users: tab === "latency" && params.get("users") === "all" ? "all" : "new",
    turns: tab === "latency" && params.get("turns") === "idle" ? "idle" : "all",
  };
}

/** The path + query a route should appear as. */
export function formatRoute(route: Partial<Route>): string {
  const tab = route.tab ?? DEFAULT_TAB;
  if (tab === "waitlist" && route.waitlistId) {
    return `/waitlist?id=${encodeURIComponent(route.waitlistId)}`;
  }
  if (tab === "sessions" && route.msid) {
    return `/sessions/${encodeURIComponent(route.msid)}`;
  }
  if (tab === "users" && route.q) {
    return `/users?q=${encodeURIComponent(route.q)}`;
  }
  // Only the non-default env is spelled out, so /tasks stays the clean link.
  if (tab === "latency") {
    // Only non-defaults are spelled out, so /latency stays the clean link.
    const params = new URLSearchParams();
    if (route.env && route.env !== DEFAULT_TASK_ENV) params.set("env", route.env);
    if (route.users === "all") params.set("users", "all");
    if (route.turns === "idle") params.set("turns", "idle");
    const qs = params.toString();
    return qs ? `/latency?${qs}` : "/latency";
  }
  if (tab === "tasks" && route.env && route.env !== DEFAULT_TASK_ENV) {
    return `/tasks?env=${route.env}`;
  }
  return `/${tab}`;
}

/** Fired on every programmatic navigation; `popstate` covers back/forward. */
const ROUTE_EVENT = "bean-admin:route";

/**
 * Move to a route.
 *
 * `replace` for changes the user did not deliberately navigate to — typing in
 * the Users search box, say. Pushing a history entry per keystroke would make
 * the back button useless.
 */
export function navigate(route: Partial<Route>, opts: { replace?: boolean } = {}) {
  const url = formatRoute(route);
  if (url === window.location.pathname + window.location.search) return;
  if (opts.replace) window.history.replaceState(null, "", url);
  else window.history.pushState(null, "", url);
  window.dispatchEvent(new Event(ROUTE_EVENT));
}

/** The current route, re-rendering on back/forward and on navigate(). */
export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(parseRoute);
  useEffect(() => {
    const sync = () => setRoute(parseRoute());
    window.addEventListener("popstate", sync);
    window.addEventListener(ROUTE_EVENT, sync);
    return () => {
      window.removeEventListener("popstate", sync);
      window.removeEventListener(ROUTE_EVENT, sync);
    };
  }, []);
  return route;
}

/** Absolute URL for a route — what a Copy link button puts on the clipboard. */
export function absoluteUrl(route: Partial<Route>): string {
  return `${window.location.origin}${formatRoute(route)}`;
}
